import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { DateTime } from 'luxon';
import {
  formatPrice,
  formatMillions,
  dateFilterParams,
  headerInfoTooltipDef,
  formatLongPrice,
  dateValueGetter,
} from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const priceFilters = [
  {
    label: '$0 - $5',
    rules: (value: number) => {
      return value <= 5;
    },
  },
  {
    label: '$5 - $10',
    rules: (value: number) => {
      return value >= 5 && value <= 10;
    },
  },
  {
    label: '$10 - $15',
    rules: (value: number) => {
      return value >= 10 && value <= 15;
    },
  },
  {
    label: '$15 - $20',
    rules: (value: number) => {
      return value >= 15 && value <= 20;
    },
  },
  {
    label: 'Greater than $20',
    rules: (value: number) => {
      return value >= 20;
    },
  },
];

const OfferingFilter = [
  {
    label: '$0 to $100M',
    rules: (value: number) => {
      return value >= 0 && value <= 100000000;
    },
  },
  {
    label: '$100M to $250M',
    rules: (value: number) => {
      return value >= 100000000 && value <= 250000000;
    },
  },
  {
    label: '$250M to $500M',
    rules: (value: number) => {
      return value >= 250000000 && value <= 500000000;
    },
  },
  {
    label: '$500M to $2B',
    rules: (value: number) => {
      return value >= 250000000 && value <= 2000000000;
    },
  },
  {
    label: '$2B+',
    rules: (value: number) => {
      return value >= 2000000000;
    },
  },
];

export default [
  {
    cellClass: 'ticker-cell',
    field: 'date',
    headerName: 'Expected IPO Date',
    ...headerInfoTooltipDef(
      'Date set during underwriting process for security to be acquired by the public market for the first time.',
    ),
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    initialWidth: 130,
    valueFormatter: ({ value }) => DateTime.fromISO(value).toFormat('MM/dd/yyyy'),
    valueGetter: ({ data }) => dateValueGetter(data.date),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    field: 'ticker',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 90,
    ...headerInfoTooltipDef('Symbol associated with the company'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'name',
    filter: 'agSetColumnFilter',
    headerName: 'Name',
    ...headerInfoTooltipDef('Full legal name of a company that corresponds to a ticker.'),
    cellRenderer: ({ data }: any) => {
      return (
        <div className="company-name-cell">
          <span>{data.name}</span>
          {data.logo && <img alt="" className="bz-ag-table__company-image" loading="lazy" src={data.logo}></img>}
        </div>
      );
    },
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 200,
  },
  {
    cellClass: 'ticker-cell',
    field: 'pricing_date',
    headerName: 'Pricing Date',
    ...headerInfoTooltipDef(
      'The date of execution and delivery of an underwriting or purchase agreement between the Company and the underwriting banks.',
    ),
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    initialWidth: 130,
    valueFormatter: ({ value }) => {
      if (value) {
        return DateTime.fromISO(value).toFormat('MM/dd/yyyy');
      }
      return '-';
    },
    valueGetter: ({ data }) => dateValueGetter(data.pricing_date),
  },
  {
    cellClass: 'ticker-cell',
    field: 'price_min',
    headerName: 'Price Min',
    ...headerInfoTooltipDef(
      'Range is determined on Offering Value and Offering shares. Determination: (Offering Value / Offering Shares).',
    ),
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: priceFilters,
    },
    initialWidth: 110,
    valueFormatter: ({ value }) => formatPrice(value),
    valueGetter: ({ data }) => Number(Number(data.price_min).toLocaleString('en')),
  },
  {
    cellClass: 'ticker-cell',
    field: 'price_max',
    headerName: 'Price Max',
    ...headerInfoTooltipDef(
      'Range is determined on Offering Value and Offering shares. Determination: (Offering Value / Offering Shares).',
    ),
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: priceFilters,
    },
    initialWidth: 110,
    valueFormatter: ({ value }) => formatPrice(value),
    valueGetter: ({ data }) => Number(Number(data.price_max).toLocaleString('en')),
  },
  {
    cellClass: 'ticker-cell',
    field: 'insider_lockup_date',
    headerName: 'Insider Lockup Date',
    ...headerInfoTooltipDef(
      'End Date of time period after an IPO, preventing company insiders from selling shares.  Usually 90 – 180 days.',
    ),
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    initialWidth: 180,
    valueFormatter: ({ value }) => {
      if (value) {
        return DateTime.fromISO(value).toFormat('MM/dd/yyyy');
      }
      return '-';
    },
    valueGetter: ({ data }) => dateValueGetter(data.insider_lockup_date),
  },
  {
    cellClass: 'ticker-cell',
    field: 'offering_value',
    headerName: 'Offering Value',
    ...headerInfoTooltipDef('The amount of capital that the company is looking to raise.'),
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: OfferingFilter,
    },
    initialWidth: 140,
    valueFormatter: ({ value }) => formatMillions(value, true),
  },
  {
    cellClass: 'ticker-cell',
    field: 'offering_shares',
    headerName: 'Offering Shares',
    ...headerInfoTooltipDef(
      'Number of shares available at public offering, which is typically determined after Offering Value is set.',
    ),
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: [
        {
          label: '0 - 1M',
          rules: (value: number) => {
            return value <= 1000000;
          },
        },
        {
          label: '1M - 2M',
          rules: (value: number) => {
            return value >= 1000000 && value <= 2000000;
          },
        },
        {
          label: '2M - 5M',
          rules: (value: number) => {
            return value >= 2000000 && value <= 5000000;
          },
        },
        {
          label: '5M - 10M',
          rules: (value: number) => {
            return value >= 5000000 && value <= 10000000;
          },
        },
        {
          label: 'Greater than 10M',
          rules: (value: number) => {
            return value >= 10000000;
          },
        },
      ],
    },
    initialWidth: 150,
    valueFormatter: ({ value }) => formatLongPrice(value, ''),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
